import "./CreateDefaultHoursPopup.scss";
import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import { toast } from "react-toastify";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import { IWeekdayHoursFacility } from "../../../types/facility";
import { createDefaultHours } from "../../../api/facility/facilityOpeningTimes";

import {
  COURT_LABELS,
  COURT_TYPES,
  FacilityCourtType,
} from "../../../pages/AdminHandleFacility/mergeHours";

interface ICreateDefaultHoursPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function CreateDefaultHoursPopup(props: ICreateDefaultHoursPopupProps) {
  const { showPopup, onClose, onConfirm } = props;

  // Starts empty and has to be chosen. Defaulting to every court meant a
  // mis-click wrote three settings instead of one, and the writes are not
  // atomic — undoing that is three deletions across three lists.
  const [selectedCourtTypes, setSelectedCourtTypes] = useState<
    FacilityCourtType[]
  >([]);

  const toggleCourtType = (type: FacilityCourtType) => {
    setSelectedCourtTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };
  const [showHandleConfirmationPopup, setShowHandleConfirmationPopup] =
    useState(false);
  const handleConfirm = () => {
    onConfirm();
    handleClose();
  };
  const handleClose = () => {
    setDefaultHourData(initialDefaultData);
    setSelectedCourtTypes([]);
    setStartTime("07:00:00");
    setEndTime("23:00:00");
    onClose();
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [startTime, setStartTime] = useState("07:00:00");
  const [endTime, setEndTime] = useState("23:00:00");
  const initialDefaultData: IWeekdayHoursFacility = {
    weekday: "monday",
    opening_hours: "07:00:00",
    closing_hours: "23:00:00",
    court_type: COURT_TYPES[0],
  };

  const [defaultHourData, setDefaultHourData] =
    useState<IWeekdayHoursFacility>(initialDefaultData);

  const weekdays = {
    Måndag: "monday",
    Tisdag: "tuesday",
    Onsdag: "wednesday",
    Torsdag: "thursday",
    Fredag: "friday",
    Lördag: "saturday",
    Söndag: "sunday",
  };
  const handleTimeChange = (
    timeType: "start" | "end",
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const timeValue = e.target.value;
    if (timeType === "start") {
      setStartTime(timeValue);
    } else {
      setEndTime(timeValue);
    }
  };

  const handleTimeBlur = (
    timeType: "start" | "end",
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    let time = e.target.value;
    let parts = time.split(":");

    if (parts.length === 2) {
      time += ":00";
    } else if (parts.length === 1 && time.includes(":")) {
      time += "00:00";
    } else if (parts.length === 1) {
      time = time.padStart(2, "0") + ":00:00";
    }

    if (timeType === "start") {
      setStartTime(time);
    } else {
      setEndTime(time);
    }
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    setDefaultHourData({
      ...defaultHourData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCreateDefaultHours = async () => {
    setLoading(true);
    defaultHourData.opening_hours = startTime;
    defaultHourData.closing_hours = endTime;
    if (selectedCourtTypes.length === 0) {
      toast.error("Välj minst en typ av bana");
      setLoading(false);
      return;
    }

    const succeeded: string[] = [];
    const failed: string[] = [];

    for (const type of selectedCourtTypes) {
      try {
        const response = await createDefaultHours({
          ...defaultHourData,
          court_type: type,
        });
        if (response && !("code" in response)) {
          succeeded.push(COURT_LABELS[type]);
        } else {
          failed.push(COURT_LABELS[type]);
        }
      } catch (error: any) {
        failed.push(COURT_LABELS[type]);
      }
    }

    setLoading(false);

    if (succeeded.length > 0) {
      toast.success(`Grundinställning skapad: ${succeeded.join(", ")}`);
    }
    if (failed.length > 0) {
      toast.error(`Misslyckades för: ${failed.join(", ")}`);
    }
    if (succeeded.length > 0) {
      handleConfirm();
    }
  };

  useEffect(() => {
    if (showPopup) {
      setDefaultHourData(initialDefaultData);
      setSelectedCourtTypes([]);
    }
  }, [showPopup]);
  return (
    <Popup
      showPopup={props.showPopup}
      onClose={handleClose}
      leftTopElement={<span className="text-m">Skapa grundinställning</span>}
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
            setShowHandleConfirmationPopup(true);
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <div className="general-popup-flex-between">
                <label htmlFor="weekday" className="text-s">
                  Veckodag
                </label>
                <select
                  className="text-m"
                  name="weekday"
                  id="weekday"
                  value={defaultHourData.weekday}
                  onChange={(e) => handleInputChange(e)}
                >
                  {Object.entries(weekdays).map(
                    ([swedishName, englishValue]) => (
                      <option value={englishValue} key={englishValue}>
                        {swedishName}
                      </option>
                    )
                  )}
                </select>
              </div>
              <label htmlFor="courtType" className="text-s">
                Typ av bana
              </label>
              {COURT_TYPES.map((option) => (
                <div key={option} className="general-popup-flex-between">
                  <span className="text-m secondary-text">
                    {COURT_LABELS[option]}
                  </span>
                  <label className="toggle text-m">
                    <input
                      type="checkbox"
                      name={`court_type_${option}`}
                      checked={selectedCourtTypes.includes(option)}
                      onChange={() => toggleCourtType(option)}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              ))}
              <div className="general-popup-flex-between">
                <label htmlFor="memberType" className="text-s">
                  Start
                </label>
                <input
                  type="time"
                  id="startTime"
                  value={startTime}
                  className="text-m"
                  onBlur={(e) => handleTimeBlur("start", e)}
                  onChange={(e) => handleTimeChange("start", e)}
                />
              </div>

              <div className="general-popup-flex-between">
                <label htmlFor="memberType" className="text-s">
                  Slut
                </label>
                <input
                  type="time"
                  id="endTime"
                  value={endTime}
                  className="text-m"
                  onBlur={(e) => handleTimeBlur("end", e)}
                  onChange={(e) => handleTimeChange("end", e)}
                />
              </div>
            </div>
          </div>

          <div className="general-popup-bottom-buttons-wrapper flex-end">
            <button type="submit" className="text-m">
              Skapa grundinställning
            </button>
          </div>
        </form>
      </div>

      <HandleConfirmationPopup
        showPopup={showHandleConfirmationPopup}
        onClose={() => setShowHandleConfirmationPopup(false)}
        onConfirm={() => {
          handleCreateDefaultHours();
          setShowHandleConfirmationPopup(false);
        }}
        confirmText="Ja"
        questionText="Är du säker på att du vill skapa upp denna grundinställning?"
        denyText="Avbryt"
      />
    </Popup>
  );
}

export default CreateDefaultHoursPopup;
